import { RefBuilder } from '@framework/repository';
import { trpcClient } from '@framework/trpcClient';
import type { GroupMemberRole } from '@group/domain';
import type { UserEntity } from '@user/UserEntity';
import { RTDBPath } from 'balus-common';
import { useEffect, useState } from 'react';

export type GroupMember = Pick<UserEntity, 'id' | 'name' | 'mailAddress' | 'imageUrl'> & { role: GroupMemberRole };

/**
 * グループの指定したIDのメンバー情報を取得
 * @param groupId グループID
 * @param userId ユーザID
 * @returns メンバー情報
 */
export const useGroupMember = (groupId: string, userId: string): GroupMember | null => {
  const [groupMember, setGroupMember] = useState<GroupMember | null>(null);

  useEffect(() => {
    const path = RTDBPath.Group.memberRolePath(groupId, userId);
    const ref = RefBuilder.ref(path);

    const callback = async () => {
      const members = await trpcClient.group.fetchGroupMembers.query({ groupId, userId });
      const member = members && members.length > 0 ? members[0] : null;
      setGroupMember(member);
    };
    const wrappedCallback = () => {
      callback().catch(console.error);
    };

    ref.on('value', wrappedCallback);
    return () => {
      ref.off('value', wrappedCallback);
    };
  }, [groupId, userId]);

  return groupMember;
};

type MembersState = { groupId: string; members: GroupMember[] | null; failed: boolean };

/**
 * グループ全員のメンバー情報を取得
 * @param groupId グループID
 * @returns メンバー情報
 */
export const useGroupMembers = (groupId: string): { members: GroupMember[] | null; failed: boolean } => {
  const [state, setState] = useState<MembersState | null>(null);

  useEffect(() => {
    setState(null);
    let active = true;
    let latestRequestId = 0;
    const ref = RefBuilder.ref(RTDBPath.Group.memberRolesPath(groupId));
    const handleFailure = (error: unknown) => {
      console.error(error);
      setState((current) => ({
        groupId,
        members: current?.groupId === groupId ? current.members : null,
        failed: true,
      }));
    };
    const callback = () => {
      const requestId = ++latestRequestId;
      trpcClient.group.fetchGroupMembers.query({ groupId, userId: null }).then(
        (members) => {
          if (!active || requestId !== latestRequestId) return;
          setState({ groupId, members, failed: false });
        },
        (error: unknown) => {
          if (!active || requestId !== latestRequestId) return;
          handleFailure(error);
        }
      );
    };
    ref.on('value', callback, (error) => {
      if (!active) return;
      ++latestRequestId;
      handleFailure(error);
    });
    return () => {
      active = false;
      ref.off('value', callback);
    };
  }, [groupId]);

  const current = state?.groupId === groupId ? state : null;
  return { members: current?.members ?? null, failed: current?.failed ?? false };
};
